'use client'

import { useMemo, useState, JSX } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { isNil, sumBy } from 'lodash'
import { EllipsisVertical, Plus } from 'lucide-react'
import { useSearchParams } from 'next/navigation'

import { Card } from '~/components/Card'
import { SplitsLink as Link } from '~/components/Link'
import { ReceiveDialogContent } from '~/components/ReceiveDialog'
import { AccountActionButtons } from '~/components/account-actions/AccountActionButtons'
import { AccountActionsMenu } from '~/components/account-detail/AccountActionsMenu'
import { DetailDialogContent } from '~/components/account-detail/DetailDialogContent'
import { EarnApyBanner } from '~/components/account-detail/EarnApyBanner'
import { EarnMigrationBanner } from '~/components/account-detail/EarnMigrationBanner'
import { LargeCurrencySmallCents } from '~/components/account-detail/LargeCurrencySmallCents'
import { SwitchOrgForAccountDialog } from '~/components/account-detail/SwitchOrgForAccountDialog'
import { useEarnMigration } from '~/components/account-detail/hooks/useEarnMigration'
import { useReconcileActiveOrgForAccount } from '~/components/account-detail/hooks/useReconcileActiveOrgForAccount'
import { useShowPositionSubHeading } from '~/components/account-detail/hooks/useShowPositionSubHeading'
import { AuthenticateCTA } from '~/components/auth/AuthenticateCTA'
import { AutomationActionButtons } from '~/components/automations/AutomationActionButtons'
import {
  AddressDisplay,
  Breadcrumb,
  Button,
  Copyable,
  CurrencyAmount,
  EmptyState,
  Message,
  NetworkLogo,
  PageLayout,
  Tooltip,
} from '~/components/component-library'
import {
  SegmentedAssetsBar,
  Segment,
} from '~/components/component-library/SegmentedAssetsBar'
import { Skeleton } from '~/components/component-library/Skeleton'
import { DepositButton } from '~/components/forms/deposit/DepositButton'
import {
  AccountSignersCard,
  SignerFacePile,
  hasSignerAvatars,
} from '~/components/smart-accounts/AccountSigners'
import { MessageFeedWrapper } from '~/components/smart-accounts/MessageFeed'
import { ShowSmallBalances } from '~/components/smart-accounts/ShowSmallBalances'
import { SmartAccountBalances } from '~/components/smart-accounts/SmartAccountBalances'
import { SmartAccountNFTBalances } from '~/components/smart-accounts/SmartAccountNFTBalances'
import { WalletConnectSection } from '~/components/smart-accounts/SmartAccountWalletConnect'
import { TransactionFeed } from '~/components/smart-accounts/TransactionFeed'
import { PositionBalances } from '~/components/smart-accounts/positions'
import { AddClankerTrackedTokenDialog } from '~/components/smart-accounts/positions/clanker'
import { invalidatePositionBalancesQuery } from '~/components/smart-accounts/positions/hooks/usePositionBalances'
import { VestingBalances } from '~/components/smart-accounts/vesting/VestingBalances'
import { WORKSPACE_AUTO_EARN_CHAIN_IDS } from '~/constants/addresses'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg, useActiveOrgId, useActiveOrgMember } from '~/hooks/org'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useSmartAccountBalances } from '~/hooks/useBalances'
import { useDisplayName } from '~/hooks/useDisplayName'
import { usePrivacyMode } from '~/hooks/usePrivacyMode'
import { isAutomationAccount } from '~/hooks/useSmartAccountPermissions'
import {
  SmartAccountBalancesGroupBy,
  useOrgSmartAccount,
  useSmartAccount,
  useSmartAccountDisplayedBalances,
  useSmartAccounts,
  useSmartAccountsNFTBalances,
} from '~/hooks/useSmartAccounts'
import {
  useCanCreateTransaction,
  useTransactions,
} from '~/hooks/useTransactions'
import { useSmartAccountsShowSmallBalances } from '~/hooks/utils'
import { HIDDEN_ADDRESS } from '~/store/privacyModeStore'
import {
  IAddress,
  OrgSmartAccount,
  PublicSmartAccount,
  SmartAccountRole,
} from '~/types'
import { EvmAddress } from '~/utils/address'
import { shortenENS } from '~/utils/addresses'
import { useIsMobile } from '~/utils/devices'
import { getSmartAccountName } from '~/utils/smartAccounts'
import { cn } from '~/utils/tailwind-utils'
import { getTokenColor } from '~/utils/tokens'
import {
  isAutoEarnEnabled,
  isWorkspaceAutoEarnEnabled,
  needsEarnMigrationFirst,
  supportsAutoEarn,
} from '~/utils/vault'

export const SmartAccountDetail = ({
  address,
}: {
  address: IAddress
}): JSX.Element => {
  const isMobile = useIsMobile()
  const { user: authUser, initialLoadingState } = useAuthUser()
  const activeOrgId = useActiveOrgId()
  const isAdmin = !!authUser?.isSplitsAdmin

  const { smartAccount, isLoading: isLoadingSmartAccount } =
    useSmartAccount(address)
  const isOrgSmartAccount = !!smartAccount && !smartAccount.isPublicSmartAccount

  const { isReconciling, state: reconcileState } =
    useReconcileActiveOrgForAccount({
      authUser,
      smartAccount,
    })

  const { totalValue } = useSmartAccountBalances(address, {
    allChains: smartAccount?.isPublicSmartAccount,
  })

  const isLoading = isLoadingSmartAccount

  const hasAccess =
    !!authUser &&
    !!smartAccount &&
    smartAccount.orgId === activeOrgId &&
    isOrgSmartAccount
  const isPublicView = !isAdmin || !hasAccess

  // get the number of transactions
  const { transactions, isInitialLoading } = useTransactions({
    smartAccountOrgId: smartAccount?.orgId,
    smartAccountId: smartAccount?.id,
    hideDust: true,
  })

  const isRootAccount =
    isOrgSmartAccount && smartAccount.role === SmartAccountRole.ROOT

  const isAutomation = isAutomationAccount(smartAccount)

  const showGettingStartedState = useMemo(
    () =>
      // Do not show the getting started state for the root account or automation accounts
      !isRootAccount &&
      !isAutomation &&
      !transactions?.length &&
      (isNil(totalValue) || totalValue === 0) &&
      !isLoading &&
      !isInitialLoading,
    [
      transactions,
      totalValue,
      isLoading,
      isInitialLoading,
      isRootAccount,
      isAutomation,
    ],
  )

  // For unauthenticated users, show minimal loading state instead of skeleton
  if (!authUser && !initialLoadingState && isLoading) {
    return (
      <PageLayout>
        <></>
      </PageLayout>
    )
  }

  // While the cross-org switch dialog is up (or while the org swap is in
  // flight after the user confirms), render a minimal placeholder so the
  // user doesn't briefly see the wrong-org public view behind the modal,
  // and so the ?transactionId param survives the swap.
  if (isReconciling) {
    return (
      <PageLayout>
        {reconcileState.kind === 'prompt' && (
          <SwitchOrgForAccountDialog
            ownerOrgName={reconcileState.ownerOrgName}
            onConfirm={reconcileState.onConfirm}
            onCancel={reconcileState.onCancel}
          />
        )}
      </PageLayout>
    )
  }

  if (isPublicView && smartAccount && smartAccount.isPublicSmartAccount) {
    return (
      <PageLayout>
        <PublicSmartAccountView
          isLoggedIn={!!authUser}
          smartAccount={smartAccount}
        />
      </PageLayout>
    )
  }

  if (!smartAccount && !isLoading) {
    return (
      <PageLayout>
        <EmptyState title={'Account not found'}>
          <p>Please check the address and try again.</p>
          <p>
            Looking for a protocol contract?{' '}
            <Link
              path={`https://explorer.splits.org/accounts/${address}`}
              target="tab"
              underline
            >
              View this address on the explorer
            </Link>
            .
          </p>
        </EmptyState>
      </PageLayout>
    )
  }

  const hideAssetsAndSettings = isRootAccount && !isLoading
  const showActions = hasAccess && !hideAssetsAndSettings

  if (showGettingStartedState) {
    return (
      <PageLayout
        breadcrumbs={
          <SmartAccountBreadcrumbs
            address={address}
            isPublicView={isPublicView}
          />
        }
        secondaryButtons={
          !hideAssetsAndSettings && (
            <SmartAccountSettingsButtons
              address={address}
              hasAccess={hasAccess}
            />
          )
        }
      >
        <AccountGettingStartedState
          address={address}
          showActions={showActions}
        />
      </PageLayout>
    )
  }

  return (
    <PageLayout
      breadcrumbs={
        <SmartAccountBreadcrumbs
          address={address}
          isPublicView={isPublicView}
        />
      }
      secondaryButtons={
        !hideAssetsAndSettings && (
          <SmartAccountSettingsButtons
            address={address}
            hasAccess={hasAccess}
          />
        )
      }
      rightSidebar={
        !hideAssetsAndSettings && <SmartAccountAssets address={address} />
      }
      header={
        <SmartAccountNameDisplay address={address} showActions={showActions} />
      }
      title={
        !hideAssetsAndSettings && <SmartAccountAssetsHeader address={address} />
      }
    >
      <SmartAccountToolbar address={address} />
      {isMobile && <SmartAccountAssets address={address} />}
      <SmartAccountFeed isAdmin={isAdmin} address={address} />
    </PageLayout>
  )
}

const AccountGettingStartedState = ({
  address,
  showActions,
}: {
  address: IAddress
  showActions: boolean
}) => {
  return (
    <div className="m-[0_auto] flex h-full w-full max-w-[460px] flex-grow flex-col space-y-6 p-3 md:pt-12">
      <SmartAccountNameDisplay address={address} showActions={showActions} />
      <div className="h-px w-full bg-gray-200 dark:bg-gray-800" />
      <ReceiveDialogContent address={address} />
    </div>
  )
}

const SmartAccountNameDisplay = ({
  address,
  showActions,
}: {
  address: IAddress
  showActions: boolean
}) => {
  const isMobile = useIsMobile()
  const { smartAccount, isLoading } = useOrgSmartAccount(address)
  const { ens } = useDisplayName(address)
  const isPrivacyModeOn = usePrivacyMode()

  if (isLoading) {
    return (
      <div className="hidden items-center gap-3 lg:flex">
        <Skeleton className="h-9" length={16} />
      </div>
    )
  }

  if (!smartAccount) {
    return null
  }

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 flex-col gap-3 md:flex-row md:items-center">
        <AddressDisplay
          address={smartAccount.address}
          layout="standard"
          size="large"
          isInternal
          hidePopover
        />
        <div className="flex items-center text-secondary">
          <Tooltip
            content={
              isPrivacyModeOn
                ? HIDDEN_ADDRESS
                : EvmAddress.format(smartAccount.address)
            }
          >
            <Copyable
              layout="standard"
              value={smartAccount.address}
              displayValue={buildHeaderAddress({ ens, isPrivacyModeOn })}
              truncateMiddle={4}
              successMessage="Address copied"
            />
          </Tooltip>
        </div>
      </div>
      {isMobile && showActions && (
        <MobileAccountActions smartAccount={smartAccount} />
      )}
    </div>
  )
}

const MobileAccountActions = ({
  smartAccount,
}: {
  smartAccount: OrgSmartAccount
}) => {
  const {
    isViewOnly,
    walletConnectOpen,
    setWalletConnectOpen,
    onConnectToApps,
  } = useConnectToApps(smartAccount)

  return (
    <div className="flex shrink-0 items-center gap-2">
      {!isViewOnly && (
        <WalletConnectSection
          address={smartAccount.address}
          connectOpen={walletConnectOpen}
          onConnectOpenChange={setWalletConnectOpen}
        />
      )}
      <AccountActionsMenu
        smartAccount={smartAccount}
        surface="page"
        trigger="icon"
        onConnectToApps={onConnectToApps}
      />
    </div>
  )
}

// WalletConnectSection must stay mounted beside the menu so its session
// listeners run; the menu only toggles the pairing dialog.
const useConnectToApps = (smartAccount: OrgSmartAccount | undefined) => {
  const isViewOnly = isAutomationAccount(smartAccount)
  const canCreateTransaction = useCanCreateTransaction(smartAccount)
  const [walletConnectOpen, setWalletConnectOpen] = useState(false)
  const onConnectToApps =
    !isViewOnly && canCreateTransaction
      ? () => setWalletConnectOpen(true)
      : undefined

  return {
    isViewOnly,
    walletConnectOpen,
    setWalletConnectOpen,
    onConnectToApps,
  }
}

// The copyable keeps the real address as its value, so copying still works.
const buildHeaderAddress = ({
  ens,
  isPrivacyModeOn,
}: {
  ens?: string
  isPrivacyModeOn: boolean
}) => {
  if (isPrivacyModeOn) return HIDDEN_ADDRESS
  if (ens) return shortenENS(ens)
  return undefined
}

const SmartAccountFeed = ({
  address,
  isAdmin,
}: {
  address: IAddress
  isAdmin: boolean
}) => {
  const { smartAccount, isLoading } = useOrgSmartAccount(address)
  const isOrgSmartAccount = !!smartAccount && !smartAccount.isPublicSmartAccount

  if (!smartAccount && !isLoading) {
    return null
  }

  return (
    <div className="flex-grow space-y-4">
      {isOrgSmartAccount && smartAccount.hasBlockingTransaction && (
        <Message variant="warning">
          Networks are out of sync. Email support@splits.org for assistance.
        </Message>
      )}
      {isOrgSmartAccount && (
        <MessageFeedWrapper
          smartAccountId={smartAccount.id}
          smartAccountAddress={EvmAddress.format(smartAccount.address)}
        />
      )}
      <div className="bg-white lg:bg-transparent dark:bg-black lg:dark:bg-transparent">
        <TransactionFeed
          smartAccountId={smartAccount?.id}
          smartAccountOrgId={isAdmin ? smartAccount?.orgId : undefined}
        />
      </div>
    </div>
  )
}

const SmartAccountAssets = ({ address }: { address: IAddress }) => {
  const { showSmallBalances, setShowSmallBalances } =
    useSmartAccountsShowSmallBalances()

  const { smartAccount } = useOrgSmartAccount(address)
  const { org } = useActiveOrg()
  const orgMember = useActiveOrgMember()
  const { smartAccounts } = useSmartAccounts()
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const queryClient = useQueryClient()
  const { show: showEarnMigration, isLoading: isLoadingEarnMigration } =
    useEarnMigration(smartAccount)

  const { balances: nftBalances } = useSmartAccountsNFTBalances({
    smartAccounts: smartAccount ? [smartAccount] : [],
    refreshInterval: 60_000,
  })

  const isOwner = orgMember?.role === 'OWNER'
  const treasury = smartAccounts.find(
    (account) => account.role === SmartAccountRole.TREASURY,
  )
  const isWorkspaceEarning =
    !!treasury &&
    WORKSPACE_AUTO_EARN_CHAIN_IDS.some((chainId) =>
      isWorkspaceAutoEarnEnabled({ treasury, chainId }),
    )

  // CTA turns automation on, so gate on that — not on holding a vault
  // position. Pre-migration accounts get the migration path, not the banner —
  // the migration gate below only covers the treasury and balance holders.
  // With workspace auto-earn the banner leads to the owner-only toggle in
  // Settings > General, so it's for owners until the workspace is earning.
  const yieldBannerAccount =
    smartAccount &&
    supportsAutoEarn(smartAccount) &&
    !isAutoEarnEnabled(smartAccount) &&
    !needsEarnMigrationFirst(smartAccount) &&
    isOwner &&
    !isWorkspaceEarning
      ? smartAccount
      : undefined
  const showNFTs = nftBalances && nftBalances.length > 0

  const showPositionSubHeading = useShowPositionSubHeading({
    address: EvmAddress.format(address),
  })
  const canAddClankerTrackedToken = !!org && isOwner
  const orgId = org?.id

  const apyBannerAccount =
    !showEarnMigration && !isLoadingEarnMigration
      ? yieldBannerAccount
      : undefined

  return (
    <div className="flex w-full flex-col md:space-y-6">
      <Card grow={false}>
        {showEarnMigration && <EarnMigrationBanner />}
        {apyBannerAccount && <EarnApyBanner />}
        <SmartAccountAssetsBar
          address={address}
          showSmallBalances={showSmallBalances}
        />
        {!isAutomationAccount(smartAccount) && (
          <div className="empty:hidden">
            {showPositionSubHeading && (
              <div className="flex items-center justify-between">
                <h3>Positions</h3>
                {canAddClankerTrackedToken && (
                  <Tooltip content="Add a Clanker token for tracking">
                    <Button
                      size="small"
                      variant="tertiary"
                      className="!p-1.5"
                      onClick={() => {
                        if (!orgId) return
                        setGlobalModalState({
                          isOpen: true,
                          content: (
                            <AddClankerTrackedTokenDialog
                              requestPath={`/api/positions/tracked-tokens/${orgId}`}
                              title="Add Clanker token"
                              onClose={closeGlobalModal}
                              onSuccess={() => {
                                invalidatePositionBalancesQuery(
                                  queryClient,
                                  orgId,
                                )
                              }}
                            />
                          ),
                        })
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                )}
              </div>
            )}
            <SmartAccountBalances
              address={address}
              isPosition
              showSmallBalances={showSmallBalances}
            />
            <VestingBalances address={address} />
            <PositionBalances address={address} />
          </div>
        )}
        <div>
          <div className="flex items-center justify-between">
            <h3>Tokens</h3>
            <ShowSmallBalances
              address={address}
              showSmallBalances={showSmallBalances}
              setShowSmallBalances={setShowSmallBalances}
            />
          </div>
          <SmartAccountBalances
            address={address}
            showSmallBalances={showSmallBalances}
          />
        </div>
      </Card>
      {showNFTs && (
        <Card grow={false}>
          <SmartAccountNFTBalances address={address} />
        </Card>
      )}
    </div>
  )
}

const SmartAccountSettingsButtons = ({
  address,
  hasAccess,
}: {
  address: IAddress
  hasAccess: boolean
}) => {
  const { smartAccount, isLoading } = useOrgSmartAccount(address)
  const { setGlobalModalState } = useAuth()
  const {
    isViewOnly,
    walletConnectOpen,
    setWalletConnectOpen,
    onConnectToApps,
  } = useConnectToApps(smartAccount)

  if (!smartAccount && !isLoading) {
    return null
  }

  return (
    <div className="hidden items-center space-x-2 md:flex">
      {!isViewOnly && (
        <WalletConnectSection
          address={address}
          connectOpen={walletConnectOpen}
          onConnectOpenChange={setWalletConnectOpen}
        />
      )}
      {smartAccount && hasSignerAvatars(smartAccount) && (
        <Tooltip
          content={<AccountSignersCard account={smartAccount} />}
          align="end"
        >
          <Button
            variant="secondary"
            size="small"
            className="group px-1.5"
            aria-label="View signers and details"
            eventName="clickedSmartAccountDetailDialog"
            eventProperties={{
              smartAccountId: smartAccount.id,
              smartAccountAddress: address,
            }}
            onClick={() =>
              setGlobalModalState({
                isOpen: true,
                content: <DetailDialogContent address={address} />,
              })
            }
          >
            <SignerFacePile
              account={smartAccount}
              includeEoaSigners
              size={20}
              ringClassName="ring-white group-hover:ring-gray-100 dark:ring-gray-900 dark:group-hover:ring-gray-800"
            />
          </Button>
        </Tooltip>
      )}
      {hasAccess && smartAccount && (
        <AccountActionsMenu
          smartAccount={smartAccount}
          surface="page"
          onConnectToApps={onConnectToApps}
        />
      )}
      {isLoading && !smartAccount && (
        <Button
          type="button"
          variant="secondary"
          size="small"
          aria-label="Account actions"
          isDisabled
          prefix={<EllipsisVertical />}
        >
          Options
        </Button>
      )}
    </div>
  )
}

const SmartAccountToolbar = ({
  address,
  className,
}: {
  address: IAddress
  className?: string
}) => {
  const { smartAccount, isLoading } = useOrgSmartAccount(address)

  const canCreateTransaction = useCanCreateTransaction(smartAccount)

  if (!smartAccount) {
    return null
  }

  if (smartAccount.role === SmartAccountRole.AUTOMATION) {
    return (
      <div
        className={cn(
          'no-scrollbar flex w-full min-w-0 max-w-full items-center gap-2 overflow-x-auto md:pr-8',
          className,
        )}
      >
        <AutomationActionButtons
          smartAccountId={smartAccount.id}
          isDisabled={isLoading}
        />
        <DepositButton defaultAccount={smartAccount.address} />
      </div>
    )
  }

  return (
    <AccountActionButtons
      account={smartAccount.address}
      isDisabled={isLoading || !canCreateTransaction}
      disabledReason={
        isLoading
          ? 'Loading smart account...'
          : "You're not a signer on this account"
      }
      className={className}
    />
  )
}

const SmartAccountAssetsHeader = ({
  address,
}: {
  address: IAddress
}): JSX.Element => {
  const { totalValue, isLoading } = useSmartAccountBalances(address)

  if (isLoading) {
    return (
      <div className="align-start flex h-16 flex-row items-center justify-start space-x-2">
        <Skeleton className="h-12 w-[72px]" />
        <Skeleton className="mb-2 h-6 w-8" />
      </div>
    )
  }

  return <LargeCurrencySmallCents amount={totalValue} />
}

const SmartAccountAssetsBar = ({
  address,
  allChains = false,
  showSmallBalances = false,
}: {
  address: IAddress
  allChains?: boolean
  showSmallBalances?: boolean
}) => {
  const { isLoading, balances } = useSmartAccountDisplayedBalances(address, {
    allChains,
    showSmallBalances,
    balancesGroupBy: SmartAccountBalancesGroupBy.Symbol,
  })

  // Calculate segments and total from filtered balances so bar fills 100%
  const { segments, totalValue } = useMemo(() => {
    const segments = balances.map((balance) => {
      return {
        name: balance.groupKey,
        color: getTokenColor(balance.groupKey),
        value: balance.groupTotalValue,
        items: balance.groupBalances.map((assetBalance) => {
          return {
            address: assetBalance.address,
            chainId: assetBalance.chainId,
            amount: assetBalance.amount,
            value: assetBalance.usdValue,
            symbol: assetBalance.tokenMetadata.symbol,
          }
        }),
      } satisfies Segment
    })
    return { segments, totalValue: sumBy(segments, 'value') }
  }, [balances])

  if (isLoading) {
    return (
      <div className="flex w-full flex-col">
        <Skeleton className="h-4 w-full" />
        <div className="flex items-center space-x-4 pt-2">
          <div className="flex items-center space-x-2">
            <Skeleton className="h-2 w-16" />
          </div>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-2 w-12" />
          </div>
        </div>
      </div>
    )
  }

  const showAssetsBar = totalValue > 0.005
  if (!showAssetsBar) {
    return null
  }

  return <SegmentedAssetsBar totalValue={totalValue} segments={segments} />
}

export const SmartAccountBreadcrumbs = ({
  address,
  pageName,
  isPublicView,
}: {
  address: IAddress
  pageName?: string
  isPublicView: boolean
}) => {
  const { smartAccount, isLoading } = useOrgSmartAccount(address)
  const { displayName, automation } = useDisplayName(address)

  const { user: authUser } = useAuthUser()
  const { smartAccounts } = useSmartAccounts()

  const showDropdown =
    smartAccounts &&
    smartAccounts.length > 1 &&
    !isPublicView &&
    !pageName &&
    authUser

  if (!smartAccount && !isLoading) {
    return null
  }

  return (
    <Breadcrumb
      isLoading={isLoading}
      items={[
        { label: 'Accounts', href: '/accounts/' },
        ...(smartAccount
          ? [
              {
                // Automation accounts: show the automation name (e.g. "Taxes"),
                // not the smart account name ("Taxes [Automation]").
                label: automation
                  ? automation.name
                  : showDropdown
                    ? getSmartAccountName(smartAccount)
                    : getSmartAccountName(smartAccount, displayName),
                href: showDropdown
                  ? undefined
                  : `/accounts/${smartAccount.address}/`,
              },
            ]
          : []),
        ...(pageName ? [{ label: pageName }] : []),
      ]}
    />
  )
}

const PublicSmartAccountView = ({
  smartAccount,
  isLoggedIn,
}: {
  smartAccount: PublicSmartAccount
  isLoggedIn: boolean
}) => {
  const searchParams = useSearchParams()
  const transactionId = searchParams.get('transactionId')

  const { totalValue, isLoading: isBalanceLoading } = useSmartAccountBalances(
    smartAccount.address,
    { allChains: true },
  )

  return (
    <>
      <div className="m-[0_auto] grid max-w-lg grid-cols-1 flex-col items-center space-y-8 p-4 md:mt-12 md:p-0">
        {transactionId && (
          <Message variant="warning">
            {isLoggedIn
              ? 'Switch accounts to view your transaction'
              : 'Login to view your transaction'}
          </Message>
        )}
        <div className="w-full space-y-4 rounded-2xl border border-gray-200 p-8 dark:border-gray-800">
          <div className="flex justify-between">
            <Copyable
              layout="standard"
              value={smartAccount.address}
              truncateMiddle={4}
            />
            <div>
              <NetworkLogo chainIds={smartAccount.chainIds} />
            </div>
          </div>
          <div>
            {isBalanceLoading ? (
              <Skeleton height={3.625} />
            ) : (
              <div className="line-clamp-1 text-5xl font-medium leading-normal">
                <CurrencyAmount layout="inline" value={totalValue} />
              </div>
            )}
          </div>
          <div>
            <SmartAccountAssetsBar
              address={smartAccount.address}
              allChains
              showSmallBalances
            />
          </div>
        </div>
        {!isLoggedIn && <AuthenticateCTA />}
      </div>
    </>
  )
}
